import FormattedMessage from 'I18n/components/FormattedMessage';
import httpClient from 'hub-http/clients/apiClient';
import Raven from 'raven-js';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { createRpcClientV1 } from 'rpc-client-utils';
import { CRM_SUITE_STARTER_BUNDLE, HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { FREE, STARTER } from 'self-service-api/constants/ProductLevels';
import { UPGRADE_PAGE } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { FeatureTableBadge } from 'ui-addon-upgrades/featureTable/sharedComponents';
import { getTranslatedGrowthFeaturesFromProductsRpc } from 'feature-store-service-types';
import { getI18nLangLocale } from 'pricing-pages-lib/utils/languageUtils';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import CurrentPlanButton from 'pricing-pages-ui/upgrade-page/CurrentPlanButton';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import { GENERAL } from 'self-service-api/constants/UpgradeProducts';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import SkeletonLoadWrapper from '../../components/common/loading/SkeletonLoadWrapper';
import { CrmSuitePrice } from '../../components/merch-card/price-display/CrmSuitePrice';
import { FREE_SUITE_STARTER_FEATURE_TABLE_CONTAINER } from '../../constants/ErrorBoundaryNames';
import { APP } from '../../constants/tracking/App';
import { SCREENS, SUITE_FEATURE_TABLE_HEADER, UPGRADE_PAGES } from '../../constants/tracking/Locations';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { withErrorBoundary } from '../ErrorBoundary';
import CrmSuiteStarterCheckoutButton from '../purchase-motion-buttons/CrmSuiteStarterCheckoutButton';
import FeatureTableFreePriceDisplay from './FeatureTableFreePriceDisplay';
import { subCategoryToFeatureIdsMap } from './featureIdToSubCategoryMap';
import { featureSubCategories } from './types';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ctaStyles = {
  width: '154px',
  padding: '11px'
};
const buildFreeSuiteStarterHeaderData = (hasPricingAndPurchasingSucceeded, purchaseMotions, handlePurchaseMotionButtonClick, upgradeData, upgradePointApiName) => {
  return {
    FREE: {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'products.crm.FREE'
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "m-y-1",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            heights: [19],
            widths: [190],
            className: "flex-column m-y-2",
            Content: FeatureTableFreePriceDisplay
          })
        }), /*#__PURE__*/_jsx(CurrentPlanButton, {
          ctaStyles: Object.assign({}, ctaStyles, {
            visibility: 'visible',
            alignSelf: 'center'
          })
        })]
      })
    },
    STARTER: {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FeatureTableBadge, {
          badgeStyle: {
            margin: '0 auto',
            width: 'fit-content',
            marginBottom: '8px'
          },
          badgeType: "recommended"
        }), /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "products.crm.STARTER"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "m-y-1",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            heights: [19],
            widths: [190],
            className: "flex-column m-y-2",
            Content: CrmSuitePrice,
            contentProps: {
              productLevel: STARTER,
              concise: true
            }
          })
        }), purchaseMotions.map((purchaseMotion, i) => /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
          className: "m-x-auto m-bottom-4",
          level: i + 1,
          location: UPGRADE_PAGE,
          purchaseMotions: purchaseMotions,
          upgradeData: upgradeData,
          use: i === 0 ? 'primary' : 'secondary',
          onClick: handlePurchaseMotionButtonClick,
          trackingSource: SUITE_FEATURE_TABLE_HEADER,
          overrides: {
            // Return a pre-configured button for suite starter when purchaseMotion is buyTouchless,
            // else return nothing. Checkout button won't work for other products.
            [BUY_TOUCHLESS]: upgradePointApiName === CRM_SUITE_STARTER_BUNDLE ? () => /*#__PURE__*/_jsx(CrmSuiteStarterCheckoutButton, {
              trackingSource: SUITE_FEATURE_TABLE_HEADER,
              ctaStyles: ctaStyles
            }) : undefined
          }
        }, purchaseMotion))]
      })
    }
  };
};
const buildFreeSuiteStarterBodyData = features => {
  const featuresBySubCategory = {};
  featureSubCategories.forEach(subCategory => {
    featuresBySubCategory[subCategory] = features.filter(feature => subCategoryToFeatureIdsMap[subCategory].includes(feature.featureId));
  });
  return {
    sharedData: {
      levels: [FREE, STARTER],
      subscreen: UPGRADE_PAGES.SUITE_STARTER
    },
    sectionData: {
      Attract: {
        features: featuresBySubCategory['ATTRACT'] || [],
        LabelContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'suiteFeatureTable.subCategories.ATTRACT'
        })
      },
      Build: {
        features: featuresBySubCategory['BUILD'] || [],
        LabelContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'suiteFeatureTable.subCategories.BUILD'
        })
      },
      Control: {
        features: featuresBySubCategory['CONTROL'] || [],
        LabelContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'suiteFeatureTable.subCategories.CONTROL'
        })
      },
      Resolve: {
        features: featuresBySubCategory['RESOLVE'] || [],
        LabelContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'suiteFeatureTable.subCategories.RESOLVE'
        })
      },
      Connect: {
        features: featuresBySubCategory['CONNECT'] || [],
        LabelContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'suiteFeatureTable.subCategories.CONNECT'
        })
      },
      Organize: {
        features: featuresBySubCategory['ORGANIZE'] || [],
        LabelContent: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'suiteFeatureTable.subCategories.ORGANIZE'
        })
      }
    }
  };
};
const FreeSuiteStarterFeatureTableContainer = ({
  purchaseMotions,
  handlePurchaseMotionButtonClick,
  upgradeConfig,
  upgradePointApiName
}) => {
  const [fetchedData, setFetchedData] = useState({
    features: [],
    loading: true
  });
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  useEffect(() => {
    const rpcClient = createRpcClientV1({
      hubHttpClient: httpClient
    });
    rpcClient.call(getTranslatedGrowthFeaturesFromProductsRpc, {
      queryParameters: {
        productApiNames: [HUBSPOT_FREE, CRM_SUITE_STARTER_BUNDLE],
        i18nLangLocale: getI18nLangLocale()
      }
    }).then(features => {
      setFetchedData({
        features,
        loading: false
      });
    }).catch(error => {
      Raven.captureException(error, {
        extra: {
          message: 'Failed to fetch FreeSuiteStarterFeatureTableContainer data'
        }
      });
    });
  }, []);
  if (fetchedData.loading === true) {
    return /*#__PURE__*/_jsx(_Fragment, {});
  }
  const queryParams = getQueryParams();
  const upgradeSource = queryParams.upgradeSource;
  const upgradeData = {
    app: APP,
    screen: SCREENS.UPGRADE_PAGE,
    uniqueId: upgradeConfig.upgradeKey,
    upgradeProduct: ApiNameToUpgradeProductMap[upgradePointApiName] || GENERAL,
    apiName: upgradePointApiName,
    upgradeSource,
    feature: upgradeConfig.feature
  };
  const headerData = buildFreeSuiteStarterHeaderData(hasPricingAndPurchasingSucceeded, purchaseMotions, handlePurchaseMotionButtonClick, upgradeData, upgradePointApiName);
  const bodyData = buildFreeSuiteStarterBodyData(fetchedData.features);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_FEATURE_TABLE"
    }), /*#__PURE__*/_jsx(FeatureTable, {
      headerData: headerData,
      bodyData: bodyData
      // canViewNewSeats doesn't apply to free and suite products but we need to provide a value
      ,
      canViewNewSeats: false
    })]
  });
};
export default withErrorBoundary(FreeSuiteStarterFeatureTableContainer, {
  boundaryName: FREE_SUITE_STARTER_FEATURE_TABLE_CONTAINER
});